import { useEffect, useState } from "react";
import { Button, Dropdown, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { FiCopy, FiMoreVertical, FiPlus } from "react-icons/fi";
import {
  createApiToken,
  getApiTokens,
  getAllLandlords,
  updateApiToken,
} from "../context/functions/AdminFunction";
import "../styles/tenants.css";

const DEFAULT_FORM = {
  landlordSearch: "",
  name: "",
  status: "active",
};

const EMPTY_VALUE = "-";
const TOKEN_STATUSES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
};
const MIN_LANDLORD_SEARCH_LENGTH = 2;

const ApiTokenManagement = () => {
  const [tokenData, setTokenData] = useState(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [apiTokens, setApiTokens] = useState([]);
  const [landlordOptions, setLandlordOptions] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [editTokenData, setEditTokenData] = useState(DEFAULT_FORM);
  const [editingToken, setEditingToken] = useState(null);
  const [editLandlordOptions, setEditLandlordOptions] = useState([]);
  const [selectedEditLandlord, setSelectedEditLandlord] = useState(null);
  const [isSearchingLandlords, setIsSearchingLandlords] = useState(false);
  const [isSearchingEditLandlords, setIsSearchingEditLandlords] = useState(false);
  const [isUpdatingToken, setIsUpdatingToken] = useState(false);

  const normalizeTokenList = (response) => {
    const list =
      response?.data?.api_keys ||
      response?.data?.common_api_keys ||
      response?.api_keys ||
      response?.common_api_keys ||
      response?.data?.modular_keys ||
      response?.data?.keys ||
      response?.data?.tokens ||
      response?.data?.apiKeys ||
      response?.modular_keys ||
      response?.data ||
      response?.keys ||
      response?.tokens ||
      response?.apiKeys ||
      response;

    return Array.isArray(list) ? list : [];
  };

  const getTokenName = (token) => {
    return token.name || token.keyName || token.title || EMPTY_VALUE;
  };

  const getTokenStatus = (token) => {
    return token.status || EMPTY_VALUE;
  };

  const formatTitleText = (value) => {
    if (!value) return EMPTY_VALUE;

    return value
      .toString()
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getTokenStatusLabel = (token) => {
    return formatTitleText(getTokenStatus(token));
  };

  const getLandlordLabel = (landlord) => {
    const name = `${landlord?.fname_companyName || landlord?.name || ""} ${landlord?.lname || ""}`.trim();
    return name || landlord?.email || landlord?._id || EMPTY_VALUE;
  };

  const getTokenLandlord = (token) => {
    return (
      token.landlord ||
      token.landlord_id ||
      token.landlordId ||
      token.property_management_landlord ||
      token.propertyManagementLandlord ||
      null
    );
  };

  const hasLinkedLandlord = (token) => {
    return Boolean(
      getTokenLandlord(token) ||
        token.landlord_name ||
        token.landlordName ||
        token.landlord_email ||
        token.landlordEmail
    );
  };

  const getTokenLandlordName = (token) => {
    if (token.landlord_name || token.landlordName) {
      return token.landlord_name || token.landlordName;
    }

    const landlord = getTokenLandlord(token);
    if (!landlord || typeof landlord !== "object") return EMPTY_VALUE;
    return getLandlordLabel(landlord);
  };

  const getTokenLandlordEmail = (token) => {
    if (token.landlord_email || token.landlordEmail) {
      return token.landlord_email || token.landlordEmail;
    }

    const landlord = getTokenLandlord(token);
    if (!landlord || typeof landlord !== "object") return "";
    return landlord.email || "";
  };

  const getTokenPermissionLabel = (token) => {
    const accessTypes = token.access_types || token.accessTypes;
    if (Array.isArray(accessTypes) && accessTypes.length > 0) {
      return accessTypes.map(formatTitleText).join(", ");
    }

    return hasLinkedLandlord(token) ? "Property Management" : "Modular Api";
  };

  const normalizeLandlordList = (response) => {
    const list =
      response?.landlords ||
      response?.data?.landlords ||
      response?.success_res?.landlords ||
      response?.data ||
      response;

    return Array.isArray(list) ? list : [];
  };

  const formatTokenDate = (date) => {
    if (!date) return EMPTY_VALUE;

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return EMPTY_VALUE;
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const normalizeCreatedToken = (response, fallback) => {
    const token = response?.data || response?.key || response?.token || response;
    if (token && typeof token === "object") {
      return {
        ...token,
        name: token.name || fallback.name,
        status: token.status || fallback.status,
      };
    }

    return {
      name: fallback.name,
      status: fallback.status,
      token,
    };
  };

  const getCreatedCredentials = (response) => {
    const data =
      response?.data?.data ||
      response?.data ||
      response?.success_res ||
      response ||
      {};

    return {
      clientId:
        data.client_id ||
        data.clientId ||
        data.credentials?.client_id ||
        data.credentials?.clientId ||
        "",
      clientSecret:
        data.client_secret ||
        data.clientSecret ||
        data.credentials?.client_secret ||
        data.credentials?.clientSecret ||
        "",
    };
  };

  const fetchApiTokens = async () => {
    setIsLoadingTokens(true);

    try {
      const response = await getApiTokens();
      setApiTokens(normalizeTokenList(response));
    } catch (error) {
      setApiTokens([]);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load API tokens"
      );
    } finally {
      setIsLoadingTokens(false);
    }
  };

  useEffect(() => {
    fetchApiTokens();
  }, []);

  useEffect(() => {
    if (!showCreateModal) {
      return;
    }

    const searchText = tokenData.landlordSearch.trim();
    if (searchText.length < MIN_LANDLORD_SEARCH_LENGTH) {
      setLandlordOptions([]);
      setIsSearchingLandlords(false);
      return;
    }

    if (selectedLandlord && searchText === getLandlordLabel(selectedLandlord)) {
      setLandlordOptions([]);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsSearchingLandlords(true);

      try {
        const response = await getAllLandlords(
          1,
          searchText,
          "Latest",
          "",
          "",
          "asc",
          "all",
          controller.signal
        );

        if (response?.status === "canceled") return;
        setLandlordOptions(normalizeLandlordList(response));
      } catch (error) {
        setLandlordOptions([]);
      } finally {
        setIsSearchingLandlords(false);
      }
    }, 350);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [showCreateModal, tokenData.landlordSearch, selectedLandlord]);

  useEffect(() => {
    if (!showEditModal) {
      return;
    }

    const searchText = editTokenData.landlordSearch.trim();
    if (searchText.length < MIN_LANDLORD_SEARCH_LENGTH) {
      setEditLandlordOptions([]);
      setIsSearchingEditLandlords(false);
      return;
    }

    if (
      selectedEditLandlord &&
      searchText === getLandlordLabel(selectedEditLandlord)
    ) {
      setEditLandlordOptions([]);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsSearchingEditLandlords(true);

      try {
        const response = await getAllLandlords(
          1,
          searchText,
          "Latest",
          "",
          "",
          "asc",
          "all",
          controller.signal
        );

        if (response?.status === "canceled") return;
        setEditLandlordOptions(normalizeLandlordList(response));
      } catch (error) {
        setEditLandlordOptions([]);
      } finally {
        setIsSearchingEditLandlords(false);
      }
    }, 350);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [showEditModal, editTokenData.landlordSearch, selectedEditLandlord]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "landlordSearch") {
      setSelectedLandlord(null);
    }
    setTokenData((data) => ({ ...data, [name]: value }));
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "landlordSearch") {
      setSelectedEditLandlord(null);
    }
    setEditTokenData((data) => ({ ...data, [name]: value }));
  };

  const handleCloseCreateModal = () => {
    if (isSubmitting) return;
    setShowCreateModal(false);
    setTokenData(DEFAULT_FORM);
    setSelectedLandlord(null);
    setLandlordOptions([]);
  };

  const handleSelectLandlord = (landlord) => {
    setSelectedLandlord(landlord);
    setLandlordOptions([]);
    setTokenData((data) => ({
      ...data,
      landlordSearch: getLandlordLabel(landlord),
    }));
  };

  const handleSelectEditLandlord = (landlord) => {
    setSelectedEditLandlord(landlord);
    setEditLandlordOptions([]);
    setEditTokenData((data) => ({
      ...data,
      landlordSearch: getLandlordLabel(landlord),
    }));
  };

  const handleClearEditLandlord = () => {
    setSelectedEditLandlord(null);
    setEditLandlordOptions([]);
    setEditTokenData((data) => ({ ...data, landlordSearch: "" }));
  };

  const handleOpenEditModal = (token) => {
    const landlord = getTokenLandlord(token);
    const landlordObject = landlord && typeof landlord === "object" ? landlord : null;

    setEditingToken(token);
    setSelectedEditLandlord(landlordObject);
    setEditLandlordOptions([]);
    setEditTokenData({
      landlordSearch: landlordObject ? getLandlordLabel(landlordObject) : "",
      name: getTokenName(token) === EMPTY_VALUE ? "" : getTokenName(token),
      status:
        getTokenStatus(token) === TOKEN_STATUSES.INACTIVE
          ? TOKEN_STATUSES.INACTIVE
          : TOKEN_STATUSES.ACTIVE,
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    if (isUpdatingToken) return;
    setShowEditModal(false);
    setEditingToken(null);
    setEditTokenData(DEFAULT_FORM);
    setSelectedEditLandlord(null);
    setEditLandlordOptions([]);
  };

  const handleCreateToken = async (event) => {
    event.preventDefault();

    const name = tokenData.name.trim();
    const landlordId = selectedLandlord?._id;
    if (!name) {
      toast.error("Token name is required");
      return;
    }

    if (tokenData.landlordSearch.trim() && !landlordId) {
      toast.error("Please select a landlord from the search results or clear the field");
      return;
    }

    setIsSubmitting(true);
    setCreatedToken(null);

    try {
      const payload = {
        name,
        status:
          tokenData.status === TOKEN_STATUSES.INACTIVE
            ? TOKEN_STATUSES.INACTIVE
            : TOKEN_STATUSES.ACTIVE,
      };
      if (landlordId) {
        payload.landlord_id = landlordId;
      }

      const response = await createApiToken(payload);
      const newToken = normalizeCreatedToken(response, payload);
      const credentials = getCreatedCredentials(response);

      setCreatedToken(newToken);
      setCreatedCredentials(credentials);
      await fetchApiTokens();
      setTokenData(DEFAULT_FORM);
      setSelectedLandlord(null);
      setLandlordOptions([]);
      setShowCreateModal(false);
      setShowCredentialsModal(Boolean(credentials.clientId || credentials.clientSecret));
      toast.success(response?.message || "Token created successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create API token"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateToken = async (event) => {
    event.preventDefault();

    const tokenId = editingToken?._id || editingToken?.id;
    const name = editTokenData.name.trim();
    const landlordId = selectedEditLandlord?._id;

    if (!tokenId) {
      toast.error("Token id is missing");
      return;
    }

    if (!name) {
      toast.error("Token name is required");
      return;
    }

    if (editTokenData.landlordSearch.trim() && !landlordId) {
      toast.error("Please select a landlord from the search results or clear the field");
      return;
    }

    setIsUpdatingToken(true);

    try {
      const payload = {
        name,
        status:
          editTokenData.status === TOKEN_STATUSES.INACTIVE
            ? TOKEN_STATUSES.INACTIVE
            : TOKEN_STATUSES.ACTIVE,
        landlord_id: landlordId || null,
      };

      const response = await updateApiToken(tokenId, payload);
      await fetchApiTokens();
      setShowEditModal(false);
      setEditingToken(null);
      setEditTokenData(DEFAULT_FORM);
      setSelectedEditLandlord(null);
      setEditLandlordOptions([]);
      toast.success(response?.message || "Token updated successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update API token"
      );
    } finally {
      setIsUpdatingToken(false);
    }
  };

  const getTokenValue = () => {
    if (!createdToken) return "";
    if (typeof createdToken === "string") return createdToken;
    return (
      createdToken.key ||
      createdToken.token ||
      createdToken.apiKey ||
      createdToken.api_key ||
      ""
    );
  };

  const handleCopyToken = async () => {
    const tokenValue = getTokenValue();
    if (!tokenValue) return;

    await handleCopyValue(tokenValue, "Token copied");
  };

  const handleCopyValue = async (value, successMessage = "Copied") => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const tokenValue = getTokenValue();

  return (
    <div className="api-token-page">
      <div className="header_table d-flex justify-content-between align-items-center">
        <h1>API Token Management</h1>
      </div>

      <div className="api-token-actions">
        <button
          className="api-token-primary-btn"
          type="button"
          onClick={() => setShowCreateModal(true)}
        >
          <FiPlus size={18} />
          <span>Create Token</span>
        </button>
      </div>

      <div className="api-token-layout tenants-container">

        <div className="api-token-list-head d-flex justify-content-between align-items-center">
          <div>
            <h4
              className="fw-800 mb-1"
              style={{
                color: "var(--tenants-color-text-primary)",
                fontSize: "22px",
              }}
            >
              API List
            </h4>
          </div>
          {isLoadingTokens && <Spinner size="sm" />}
        </div>

        <div className="tenants-card">
          <div className="table-responsive">
            <Table responsive hover className="tenants-table mb-0">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>PERMISSIONS</th>
                  <th>LANDLORD</th>
                  <th>STATUS</th>
                  <th>CREATED ON</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoadingTokens ? (
                  <tr>
                    <td colSpan={6}>Loading...</td>
                  </tr>
                ) : apiTokens.length === 0 ? (
                  <tr>
                    <td colSpan={6}>No API tokens found</td>
                  </tr>
                ) : (
                  apiTokens.map((token, index) => (
                    <tr key={token._id || token.id || token.name || index}>
                      <td>
                        <div className="tenant-name-stack">
                          <span className="tenant-name">
                            {getTokenName(token)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="api-token-permission-text">
                          {getTokenPermissionLabel(token)}
                        </span>
                      </td>
                      <td>
                        {hasLinkedLandlord(token) ? (
                          <div className="tenant-name-stack">
                            <span className="tenant-name">
                              {getTokenLandlordName(token)}
                            </span>
                            {getTokenLandlordEmail(token) && (
                              <span className="tenant-email">
                                {getTokenLandlordEmail(token)}
                              </span>
                            )}
                          </div>
                        ) : (
                          EMPTY_VALUE
                        )}
                      </td>
                      <td>
                        <span
                          className={
                            getTokenStatus(token) === "active"
                              ? "tenant-status-active"
                              : "tenant-status-inactive"
                          }
                        >
                          {getTokenStatusLabel(token)}
                        </span>
                      </td>
                      <td className="fw-500">{formatTokenDate(token.created_at)}</td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            as={Button}
                            size="sm"
                            className="api-token-action-btn"
                          >
                            <FiMoreVertical size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            align="end"
                            className="api-token-actions-menu"
                            popperConfig={{ strategy: "fixed" }}
                            renderOnMount
                          >
                            <Dropdown.Item
                              as="button"
                              onClick={() => handleOpenEditModal(token)}
                            >
                              Edit
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <Modal
        show={showCreateModal}
        onHide={handleCloseCreateModal}
        centered
        className="api-token-modal"
      >
        <Modal.Header closeButton className="api-token-modal-header">
          <Modal.Title>Create Token</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreateToken}>
          <Modal.Body className="api-token-modal-body">
            <label htmlFor="api-token-name">
              Name
              <InputGroup className="input_table_header">
                <Form.Control
                  id="api-token-name"
                  placeholder="Enter token name"
                  aria-label="Token name"
                  name="name"
                  type="text"
                  value={tokenData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </InputGroup>
            </label>

            <div className="api-token-landlord-search">
              <label htmlFor="api-token-landlord-search">
                Landlord
                <InputGroup className="input_table_header">
                  <Form.Control
                    id="api-token-landlord-search"
                    placeholder="Optional: type at least 2 characters to search"
                    aria-label="Search landlord"
                    name="landlordSearch"
                    type="text"
                    value={tokenData.landlordSearch}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    autoComplete="off"
                  />
                </InputGroup>
              </label>
              <p className="api-token-help-text">
                If you enter a landlord, this key can access property APIs. Without a landlord, it cannot access property APIs.
              </p>

              {(isSearchingLandlords || landlordOptions.length > 0) && (
                <div className="api-token-landlord-options">
                  {isSearchingLandlords ? (
                    <div className="api-token-landlord-option muted">
                      Searching...
                    </div>
                  ) : (
                    landlordOptions.map((landlord) => (
                      <button
                        key={landlord._id}
                        className="api-token-landlord-option"
                        type="button"
                        onClick={() => handleSelectLandlord(landlord)}
                      >
                        <span>{getLandlordLabel(landlord)}</span>
                        <small>{landlord.email || landlord._id}</small>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <label htmlFor="api-token-status">
              Status
              <Form.Select
                id="api-token-status"
                aria-label="Select token status"
                name="status"
                value={tokenData.status}
                onChange={handleInputChange}
                disabled={isSubmitting}
              >
                <option value={TOKEN_STATUSES.ACTIVE}>Active</option>
                <option value={TOKEN_STATUSES.INACTIVE}>Inactive</option>
              </Form.Select>
            </label>
          </Modal.Body>
          <Modal.Footer className="api-token-modal-footer">
            <Button
              className="api-token-secondary-btn"
              type="button"
              onClick={handleCloseCreateModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button className="api-token-primary-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" />
                  Creating
                </>
              ) : (
                <>
                  <FiPlus size={18} />
                  Create Token
                </>
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        centered
        className="api-token-modal"
      >
        <Modal.Header closeButton className="api-token-modal-header">
          <Modal.Title>Edit Token</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleUpdateToken}>
          <Modal.Body className="api-token-modal-body">
            <label htmlFor="edit-api-token-name">
              Name
              <InputGroup className="input_table_header">
                <Form.Control
                  id="edit-api-token-name"
                  placeholder="Enter token name"
                  aria-label="Token name"
                  name="name"
                  type="text"
                  value={editTokenData.name}
                  onChange={handleEditInputChange}
                  disabled={isUpdatingToken}
                />
              </InputGroup>
            </label>

            <div className="api-token-landlord-search">
              <div className="api-token-label-row">
                <label htmlFor="edit-api-token-landlord-search">
                  Landlord
                </label>
                {editTokenData.landlordSearch && (
                  <button
                    type="button"
                    onClick={handleClearEditLandlord}
                    disabled={isUpdatingToken}
                  >
                    Remove
                  </button>
                )}
              </div>
              <InputGroup className="input_table_header">
                <Form.Control
                  id="edit-api-token-landlord-search"
                  placeholder="Optional: type at least 2 characters to search"
                  aria-label="Search landlord"
                  name="landlordSearch"
                  type="text"
                  value={editTokenData.landlordSearch}
                  onChange={handleEditInputChange}
                  disabled={isUpdatingToken}
                  autoComplete="off"
                />
              </InputGroup>
              <p className="api-token-help-text">
                If you enter a landlord, this key can access property APIs. Without a landlord, it cannot access property APIs.
              </p>

              {(isSearchingEditLandlords || editLandlordOptions.length > 0) && (
                <div className="api-token-landlord-options">
                  {isSearchingEditLandlords ? (
                    <div className="api-token-landlord-option muted">
                      Searching...
                    </div>
                  ) : (
                    editLandlordOptions.map((landlord) => (
                      <button
                        key={landlord._id}
                        className="api-token-landlord-option"
                        type="button"
                        onClick={() => handleSelectEditLandlord(landlord)}
                      >
                        <span>{getLandlordLabel(landlord)}</span>
                        <small>{landlord.email || landlord._id}</small>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <label htmlFor="edit-api-token-status">
              Status
              <Form.Select
                id="edit-api-token-status"
                aria-label="Select token status"
                name="status"
                value={editTokenData.status}
                onChange={handleEditInputChange}
                disabled={isUpdatingToken}
              >
                <option value={TOKEN_STATUSES.ACTIVE}>Active</option>
                <option value={TOKEN_STATUSES.INACTIVE}>Inactive</option>
              </Form.Select>
            </label>
          </Modal.Body>
          <Modal.Footer className="api-token-modal-footer">
            <Button
              className="api-token-secondary-btn"
              type="button"
              onClick={handleCloseEditModal}
              disabled={isUpdatingToken}
            >
              Cancel
            </Button>
            <Button className="api-token-primary-btn" type="submit" disabled={isUpdatingToken}>
              {isUpdatingToken ? (
                <>
                  <Spinner size="sm" />
                  Saving
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={showCredentialsModal}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        centered
        className="api-token-modal api-token-credentials-modal"
      >
        <Modal.Header className="api-token-modal-header">
          <Modal.Title>API Credentials</Modal.Title>
        </Modal.Header>
        <Modal.Body className="api-token-credentials-body">
          <div className="api-token-credential-row">
            <div className="api-token-credential-copy">
              <span>Client ID</span>
              <code>{createdCredentials?.clientId || EMPTY_VALUE}</code>
            </div>
            <button
              type="button"
              onClick={() =>
                handleCopyValue(createdCredentials?.clientId, "Client ID copied")
              }
              aria-label="Copy client ID"
              disabled={!createdCredentials?.clientId}
            >
              <FiCopy size={18} />
            </button>
          </div>

          <div className="api-token-credential-row">
            <div className="api-token-credential-copy">
              <span>Client Secret</span>
              <code>{createdCredentials?.clientSecret || EMPTY_VALUE}</code>
            </div>
            <button
              type="button"
              onClick={() =>
                handleCopyValue(
                  createdCredentials?.clientSecret,
                  "Client secret copied"
                )
              }
              aria-label="Copy client secret"
              disabled={!createdCredentials?.clientSecret}
            >
              <FiCopy size={18} />
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer className="api-token-modal-footer">
          <Button
            className="api-token-primary-btn"
            type="button"
            onClick={() => setShowCredentialsModal(false)}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApiTokenManagement;
